import React, { useState } from 'react';
import { Button, Alert, Snackbar, Typography, Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ClientList from '../../components/clients/ClientList';
import ClientForm from '../../components/clients/ClientForm';
import { Client } from '../../types/client';
import { useRBAC } from '../../auth/RBACProvider';

const ClientsPage: React.FC = () => {
  const { hasGlobalPermission, isAdmin, loading: rbacLoading } = useRBAC();
  const [showForm, setShowForm] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | undefined>(undefined);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Check permissions
  const canManageClients = hasGlobalPermission('manage_clients') || isAdmin();
  const canViewClients = canManageClients || hasGlobalPermission('view_all_accounts') || isAdmin();

  if (rbacLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <Typography>Loading permissions...</Typography>
      </Box>
    );
  }

  if (!canViewClients) {
    return (
      <Box p={4}>
        <Typography variant="h5" color="error" gutterBottom>
          Access Denied
        </Typography>
        <Typography>
          You don't have permission to view client configurations. Please contact your administrator.
        </Typography>
      </Box>
    );
  }

  const handleAddClient = () => {
    setSelectedClient(undefined);
    setShowForm(true);
  };

  const handleEditClient = (client: Client) => {
    setSelectedClient(client);
    setShowForm(true);
  };

  const handleSave = () => {
    setShowForm(false);
    setSelectedClient(undefined);
    setRefreshTrigger(prev => prev + 1);
    setNotification({
      open: true,
      message: `Client ${selectedClient ? 'updated' : 'created'} successfully!`,
      severity: 'success'
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setSelectedClient(undefined);
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  return (
    <div className="page-content">
        <h1 className="page-title">Client Configuration</h1>
        
        {notification.open && (
          <div className={`message ${notification.severity === 'success' ? 'success' : 'error'}`}>
            {notification.message}
          </div>
        )}
        
        <div className="upload-section">
          {!showForm && canManageClients && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
              <Button
                variant="outlined"
                sx={{
                  borderColor: 'var(--e-global-color-secondary)',
                  color: 'var(--e-global-color-secondary)',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'var(--e-global-color-secondary)',
                    color: '#fff',
                    borderColor: 'var(--e-global-color-secondary)'
                  }
                }}
                startIcon={<AddIcon />}
                onClick={handleAddClient}
              >
                Add New Client
              </Button>
            </div>
          )}

          <Snackbar
            open={notification.open}
            autoHideDuration={6000}
            onClose={handleCloseNotification}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert 
              onClose={handleCloseNotification} 
              severity={notification.severity}
              sx={{ width: '100%' }}
            >
              {notification.message}
            </Alert>
          </Snackbar>

          {showForm ? (
            <ClientForm
              client={selectedClient}
              onSave={handleSave}
              onCancel={handleCancel}
            />
          ) : (
            <ClientList
              onEditClient={handleEditClient}
              refreshTrigger={refreshTrigger}
            />
          )}
        </div>
    </div>
  );
};

export default ClientsPage;
